/**
 * StorybookPage — entry point for Storybook Mode.
 * Routes: /storybook (start/list) and /storybook/:storybookId (workspace)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { BookOpen, Loader2, Plus, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArchiveEmptyState } from '../components/archive/ArchiveEmptyState';
import morrisonArchivePreview from '../assets/landing/morrison-archive-preview.jpg';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { StorybookWorkspace } from '../components/storybook/StorybookWorkspace';
import { storybookApi, storybookHref, STORYBOOK_STATUS_LABELS, STORYBOOK_TEMPLATE_OPTIONS } from '../utils/storybook';
import { getApiBase } from '../utils/apiBase';
import { getDisplayName } from '../utils/companionProfiles';
import AppFooter from '../components/AppFooter';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { ContextualGuideTip } from '../components/guide/ContextualGuideTip';

const API = getApiBase();

export default function StorybookPage() {
  const { storybookId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [storybooks, setStorybooks] = useState([]);
  const [trees, setTrees] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(!storybookId);
  const [creating, setCreating] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [startForm, setStartForm] = useState({
    treeId: searchParams.get('tree') || '',
    type: searchParams.get('type') || 'person',
    templateKey: searchParams.get('template') || 'person_storybook',
    personId: searchParams.get('person') || '',
    title: '',
  });

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [sbs, treesRes] = await Promise.all([
        storybookApi.list(),
        axios.get(`${API}/trees`),
      ]);
      setStorybooks(sbs);
      setTrees(treesRes.data || []);
    } catch {
      toast.error('Could not load storybooks.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!storybookId) loadList();
  }, [storybookId, loadList]);

  // Auto-start when query params present
  useEffect(() => {
    const tree = searchParams.get('tree');
    const person = searchParams.get('person');
    const type = searchParams.get('type') || 'person';
    if (!storybookId && tree && (person || type === 'self')) {
      handleCreate({
        treeId: tree,
        personId: person,
        type,
        templateKey: searchParams.get('template') || (type === 'self' ? 'my_life_story' : 'person_storybook'),
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPeopleForTree = async (treeId) => {
    if (!treeId) { setPeople([]); return; }
    try {
      const res = await axios.get(`${API}/individuals/${treeId}`);
      setPeople(res.data || []);
    } catch { setPeople([]); }
  };

  useEffect(() => {
    if (startOpen && startForm.treeId) loadPeopleForTree(startForm.treeId);
  }, [startOpen, startForm.treeId]);

  const handleCreate = async ({ treeId, personId, type, templateKey, title } = startForm) => {
    if (!treeId) { toast.error('Please select an archive.'); return; }
    const tpl = STORYBOOK_TEMPLATE_OPTIONS.find((t) => t.key === (templateKey || startForm.templateKey));
    const effectiveType = tpl?.storybookType || type;
    if (effectiveType === 'person' && !personId) { toast.error('Please select a person.'); return; }

    setCreating(true);
    try {
      const ws = await storybookApi.create({
        tree_id: treeId,
        storybook_type: effectiveType,
        template_key: templateKey || startForm.templateKey,
        target_person_id: effectiveType === 'person' ? personId : user?.self_person_id || personId || null,
        title: title?.trim() || undefined,
      });
      const id = ws.storybook?.id;
      if (id) {
        navigate(storybookHref(id), { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create storybook.');
    } finally {
      setCreating(false);
      setStartOpen(false);
    }
  };

  if (storybookId) {
    return (
      <>
        <StorybookWorkspace storybookId={storybookId} />
        <AppFooter />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(42_28%_97%)]">
      <header className="border-b border-[hsl(38_14%_88%)] bg-[hsl(42_28%_99%)]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-foreground">
            ← Dashboard
          </Link>
          <BrandWordmark variant="navApp" className="text-lg" />
          <div className="w-16" />
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-10">
        <ContextualGuideTip tipId="storybook" title="AI Story Studio · Storybook Mode" className="mb-6">
          Answer prompts by writing or recording. Transcribe voice, create chapter drafts, and preview your Legacy Book when ready.
        </ContextualGuideTip>
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8b7355]">
              AI Story Studio
            </p>
            <p className="mb-1 text-[11px] font-medium text-muted-foreground/80">Storybook Mode</p>
            <h1 className="font-serif text-3xl font-light text-[#1c1917]">Your storybooks</h1>
            <p className="mt-2 text-sm text-muted-foreground/80 max-w-lg">
              A guided journey through sixteen chapters — from beginnings to final reflections.
              One prompt at a time, at your own pace.
            </p>
          </div>
          <Button className="shrink-0 rounded-full gap-2" onClick={() => setStartOpen(true)}>
            <Plus className="h-4 w-4" /> Start storybook
          </Button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 py-12 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        )}

        {!loading && storybooks.length === 0 && (
          <ArchiveEmptyState
            icon={BookOpen}
            align="center"
            title="Your storybooks will live here"
            description="Begin with one scene — a childhood home, a first job, or a family tradition. Interview Mode records one question at a time."
            data-testid="storybook-empty-state"
            actions={
              <Button className="rounded-full" onClick={() => setStartOpen(true)}>
                Start your first storybook
              </Button>
            }
          >
            <div className="relative mx-auto mt-4 max-w-sm overflow-hidden rounded-xl border border-[hsl(38_14%_86%)] shadow-sm">
              <img
                src={morrisonArchivePreview}
                alt=""
                className="aspect-[16/9] w-full object-cover object-[center_35%] opacity-90 saturate-[0.85] sepia-[0.08]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1c1917]/40 to-transparent" />
              <p className="absolute bottom-3 left-3 right-3 text-left text-xs text-[#faf7f2]/90">
                Sixteen guided chapters — at your own pace
              </p>
            </div>
          </ArchiveEmptyState>
        )}

        {!loading && (
          <p className="mt-8 text-center text-[11px] text-muted-foreground/50 max-w-md mx-auto leading-relaxed">
            Interview Mode is available inside each storybook — record one question at a time.
            Your answers are preserved in your archive and included in your keepsake legacy book.
          </p>
        )}

        {!loading && storybooks.length > 0 && (
          <div className="space-y-3">
            {storybooks.map((sb) => {
              const pct = sb.book_progress_percent ?? (sb.total_prompts
                ? Math.round((sb.completed_count / sb.total_prompts) * 100)
                : 0);
              const tplLabel = STORYBOOK_TEMPLATE_OPTIONS.find((t) => t.key === sb.template_key)?.label;
              return (
                <Link
                  key={sb.id}
                  to={storybookHref(sb.id)}
                  className="flex items-center gap-4 rounded-xl border border-[hsl(38_14%_88%)] bg-white px-5 py-4 transition hover:border-[hsl(38_14%_78%)] hover:shadow-sm"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#2C4A3B]/10">
                    <BookOpen className="h-5 w-5 text-[#2C4A3B]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate text-foreground/90">{sb.title}</p>
                    <p className="text-xs text-muted-foreground/70">
                      {tplLabel && <span>{tplLabel} · </span>}
                      {STORYBOOK_STATUS_LABELS[sb.status] || sb.status}
                      {' · '}{sb.completed_count}/{sb.total_prompts} prompts · {pct}% book progress
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Start dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="sm:max-w-md border-[#e8e2d8] bg-[#fffcf7]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl font-normal">Start a storybook</DialogTitle>
            <DialogDescription>
              Choose who this life story is for. You can write your own or guide someone else&apos;s archive.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Storybook type</Label>
              <div className="grid gap-2 sm:grid-cols-2 max-h-48 overflow-y-auto pr-1">
                {STORYBOOK_TEMPLATE_OPTIONS.map((tpl) => (
                  <button
                    key={tpl.key}
                    type="button"
                    disabled={!tpl.available}
                    onClick={() => {
                      if (!tpl.available) return;
                      setStartForm((f) => ({
                        ...f,
                        templateKey: tpl.key,
                        type: tpl.storybookType,
                      }));
                    }}
                    className={`rounded-lg border px-3 py-2.5 text-left text-xs transition ${
                      startForm.templateKey === tpl.key && tpl.available
                        ? 'border-primary/40 bg-primary/8 text-primary'
                        : tpl.available
                          ? 'border-border/60 text-muted-foreground hover:border-border'
                          : 'border-border/40 text-muted-foreground/50 cursor-not-allowed opacity-60'
                    }`}
                  >
                    <span className="font-medium block">{tpl.label}</span>
                    <span className="mt-0.5 block text-[10px] leading-snug opacity-80">{tpl.description}</span>
                    {tpl.comingSoon && (
                      <span className="mt-1 inline-block text-[9px] uppercase tracking-wide text-[#8b7355]">Planned</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sb-tree" className="text-sm">Archive</Label>
              <select
                id="sb-tree"
                value={startForm.treeId}
                onChange={(e) => setStartForm((f) => ({ ...f, treeId: e.target.value, personId: '' }))}
                className="w-full rounded-lg border border-[#e8e2d8] bg-white px-3 py-2 text-sm"
              >
                <option value="">Select archive…</option>
                {trees.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {startForm.type === 'person' && startForm.templateKey !== 'my_life_story' && (
              <div className="space-y-1.5">
                <Label htmlFor="sb-person" className="text-sm">Person</Label>
                <select
                  id="sb-person"
                  value={startForm.personId}
                  onChange={(e) => setStartForm((f) => ({ ...f, personId: e.target.value }))}
                  className="w-full rounded-lg border border-[#e8e2d8] bg-white px-3 py-2 text-sm"
                  disabled={!startForm.treeId}
                >
                  <option value="">Select person…</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>{getDisplayName(p)}</option>
                  ))}
                </select>
              </div>
            )}

            {startForm.templateKey === 'my_life_story' && !user?.self_person_id && (
              <p className="text-xs text-amber-700/80 rounded-lg bg-amber-50/80 border border-amber-200/60 px-3 py-2">
                Tip: link yourself as a person in your archive from the Dashboard for the best experience.
              </p>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                className="rounded-full"
                disabled={creating}
                onClick={() => handleCreate()}
              >
                {creating ? 'Opening…' : 'Open storybook'}
              </Button>
              <Button variant="ghost" className="rounded-full" onClick={() => setStartOpen(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AppFooter />
    </div>
  );
}
